import * as RadixTabs from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Thin styling layer over @radix-ui/react-tabs. Keeps the radix accessibility
 * model (keyboard navigation, ARIA wiring, focus management) and just dresses
 * it in our HSL design tokens.
 */
export function Tabs({
  value,
  onValueChange,
  children,
  className,
}: {
  value: string;
  onValueChange: (v: string) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <RadixTabs.Root value={value} onValueChange={onValueChange} className={className}>
      {children}
    </RadixTabs.Root>
  );
}

export function TabsList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <RadixTabs.List
      className={cn(
        'inline-flex flex-wrap items-center gap-1 rounded-xl border border-border bg-card p-1',
        className,
      )}
    >
      {children}
    </RadixTabs.List>
  );
}

export function TabsTrigger({ value, children }: { value: string; children: ReactNode }) {
  return (
    <RadixTabs.Trigger
      value={value}
      className={cn(
        'rounded-lg px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors',
        'data-[state=active]:bg-secondary data-[state=active]:text-foreground',
        'hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
      )}
    >
      {children}
    </RadixTabs.Trigger>
  );
}

export function TabsContent({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <RadixTabs.Content value={value} className={cn('mt-6 focus:outline-none', className)}>
      {children}
    </RadixTabs.Content>
  );
}
